import { FormField, FormGrid } from '@dixa/lib-shared-habla';
import { DraftCustomAttributeValue } from 'lib-shared-legacy-types';
import { memo, useEffect, useMemo } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { customFieldsApi } from 'apis/customFields';
import { BaseCustomFieldSelect } from 'components/customFields/baseCustomFieldSelect/baseCustomFieldSelect';
import { BaseCustomFieldText } from 'components/customFields/baseCustomFieldText/baseCustomFieldText';
import { FieldToggleBoxHead, ToggleBox, ToggleBoxBody } from 'fable/toggleBox';
import {
  CustomField,
  isSelectCustomField,
  isTextCustomField,
} from 'utils/customFields';
import { useCustomFieldsVisibility } from 'utils/customFields/useCustomFieldsVisibility';

type ComposeCustomAttributesFieldProps = {
  /** Current custom attribute values from the form */
  readonly value?: Record<string, DraftCustomAttributeValue>;
  /** Callback when custom attribute values change */
  readonly onChange: (
    values: Record<string, DraftCustomAttributeValue>,
  ) => void;
  /** Selected queue ID for visibility filtering */
  readonly queueId?: string;
};

type ComposeCustomAttributesToggleProps = ComposeCustomAttributesFieldProps & {
  /** Whether the toggle is open */
  readonly isOpen?: boolean;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type FormValues = Record<string, any>;

/**
 * Renders custom attribute fields for the compose email modal. Reuses
 * BaseCustomFieldText and BaseCustomFieldSelect components from the sidebar.
 */
export const ComposeCustomAttributesField = memo(
  function ComposeCustomAttributesField({
    value,
    onChange,
    queueId,
  }: ComposeCustomAttributesFieldProps): JSX.Element | null {
    // Fetch conversation custom field attributes
    const { data: attributesData, isSuccess } =
      customFieldsApi.useReadAttributesQuery({
        entityId: 'conversation',
      });

    // Filter to only show active (non-archived, non-deactivated) attributes
    const attributes = useMemo(() => {
      if (!attributesData?.data?.attributes) {
        return [];
      }
      return attributesData.data.attributes.filter(
        (attr) => !attr.is_archived && !attr.is_deactivated,
      );
    }, [attributesData]);

    // Create CustomField objects for visibility evaluation and rendering
    const customFields = useMemo((): CustomField[] => {
      return attributes.map((attr) => {
        const fieldValue = value?.[attr.id];
        return {
          attribute: attr,
          value: fieldValue
            ? {
                data_type: fieldValue.data_type,
                value: fieldValue.value,
              }
            : undefined,
        } as CustomField;
      });
    }, [attributes, value]);

    // Evaluate visibility conditions based on queue and other attribute values
    const visibility = useCustomFieldsVisibility(customFields, {
      queue: queueId ? [queueId] : [],
    });

    // Convert initial values from DraftCustomAttributeValue format to form format
    const externalValues = useMemo(() => {
      const result: FormValues = {};
      if (value) {
        Object.entries(value).forEach(([attrId, attrValue]) => {
          if (attrValue.data_type === 'string') {
            result[attrId] = attrValue.value as string;
          } else if (attrValue.data_type === 'string_list') {
            // For select fields, wrap the value in an array for the form
            result[attrId] = [attrValue.value];
          }
        });
      }
      return result;
    }, [value]);

    const { control } = useForm<FormValues>({
      values: externalValues,
      mode: 'onChange',
    });

    // Watch all form values to sync back to parent
    const formValues = useWatch({ control });

    // Sync form values back to parent on change
    useEffect(() => {
      if (!attributes.length) return;

      const customAttributeValues: Record<string, DraftCustomAttributeValue> =
        {};

      attributes.forEach((attr) => {
        const fieldValue = formValues[attr.id];
        if (
          fieldValue !== undefined &&
          fieldValue !== '' &&
          fieldValue !== null
        ) {
          if (
            attr.type === 'text' &&
            typeof fieldValue === 'string' &&
            fieldValue.trim() !== ''
          ) {
            customAttributeValues[attr.id] = {
              data_type: 'string',
              value: fieldValue,
            };
          } else if (
            attr.type === 'select' &&
            Array.isArray(fieldValue) &&
            fieldValue.length > 0
          ) {
            // Select values come as nested array [[path, values]]
            const selectValue = fieldValue[0];
            if (
              selectValue &&
              (Array.isArray(selectValue)
                ? selectValue.length > 0
                : selectValue)
            ) {
              customAttributeValues[attr.id] = {
                data_type: 'string_list',
                value: selectValue,
              };
            }
          }
        }
      });

      onChange(customAttributeValues);
    }, [formValues, attributes, onChange]);

    // Filter visible custom fields
    const visibleCustomFields = useMemo(() => {
      return customFields.filter(
        (cf) => visibility.data[cf.attribute.id] !== false,
      );
    }, [customFields, visibility.data]);

    if (!isSuccess || visibleCustomFields.length === 0) {
      return null;
    }

    return (
      <FormGrid>
        {visibleCustomFields.map((customField) => {
          if (isTextCustomField(customField)) {
            return (
              <FormField key={customField.attribute.id}>
                <BaseCustomFieldText
                  customField={customField}
                  control={control}
                  // All fields are optional in compose mode
                  rules={{ required: false }}
                  noCopy
                />
              </FormField>
            );
          }

          if (isSelectCustomField(customField)) {
            return (
              <FormField key={customField.attribute.id}>
                <BaseCustomFieldSelect
                  customField={customField}
                  control={control}
                  // All fields are optional in compose mode
                  rules={{ required: false }}
                />
              </FormField>
            );
          }

          return null;
        })}
      </FormGrid>
    );
  },
);

/**
 * Wrapper component that renders custom attributes inside a toggle box. Only
 * renders if there are custom attributes defined for the organization.
 */
export const ComposeCustomAttributesToggle = memo(
  function ComposeCustomAttributesToggle({
    value,
    onChange,
    queueId,
    isOpen,
  }: ComposeCustomAttributesToggleProps): JSX.Element | null {
    // Fetch conversation custom field attributes to check if any exist
    const { data: attributesData, isSuccess } =
      customFieldsApi.useReadAttributesQuery({
        entityId: 'conversation',
      });

    // Check if there are any active attributes
    const hasActiveAttributes = useMemo(() => {
      if (!attributesData?.data?.attributes) {
        return false;
      }
      return attributesData.data.attributes.some(
        (attr) => !attr.is_archived && !attr.is_deactivated,
      );
    }, [attributesData]);

    // Don't render the toggle if there are no custom attributes
    if (!isSuccess || !hasActiveAttributes) {
      return null;
    }

    return (
      <ToggleBox
        className="custom-attributes"
        headComponent={FieldToggleBoxHead}
        isOpen={!!isOpen}
        title="Add conversation attributes"
        input={{
          id: 'customAttributesOpen',
          value: 'customAttributesOpen',
          name: 'customAttributesOpen',
        }}
      >
        <ToggleBoxBody>
          <ComposeCustomAttributesField
            value={value}
            onChange={onChange}
            queueId={queueId}
          />
        </ToggleBoxBody>
      </ToggleBox>
    );
  },
);
